import React, { useState } from "react";
import client, { apiError } from "@/lib/api";
import { Advance } from "@/lib/types";
import { money } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  advances: Advance[];
  onDone: () => void;
}

const HINTS: Record<string, string> = {
  descuento: "Se registrará un solo gasto de Nómina / Sueldos por el total, sin volver a salir dinero de caja.",
  utilidades: "Se registrará un solo Retiro de utilidades por el total, sin volver a salir dinero de caja.",
  deposito: "Se registrará una sola entrada en Caja por el total devuelto.",
};

export const BulkCompensateDialog: React.FC<Props> = ({ open, onOpenChange, advances, onDone }) => {
  const [form, setForm] = useState({ method: "descuento", date: new Date().toLocaleDateString("en-CA"), note: "" });
  const [saving, setSaving] = useState(false);
  const total = advances.reduce((s, a) => s + a.outstanding, 0);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await client.post("/advances/compensate-bulk", { advance_ids: advances.map((a) => a.id), ...form });
      toast.success(`${r.data.count} anticipo(s) compensados · ${money(r.data.total)}`);
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none" data-testid="bulk-compensate-dialog">
        <DialogHeader>
          <DialogTitle>Compensar {advances.length} anticipo(s)</DialogTitle>
          <DialogDescription>Cada anticipo se compensa por su saldo pendiente completo.</DialogDescription>
        </DialogHeader>
        <div className="max-h-40 space-y-1 overflow-y-auto border border-slate-100 bg-slate-50/50 p-3 text-xs" data-testid="bulk-compensate-list">
          {advances.map((a) => (
            <div key={a.id} className="flex justify-between text-slate-600">
              <span>{a.counterpart}</span>
              <span className="tabular">{money(a.outstanding)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-slate-200 pt-1 font-medium text-slate-900">
            <span>Total</span>
            <span className="tabular" data-testid="bulk-compensate-total">{money(total)}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Método</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="bulk-compensate-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="descuento">Nómina</SelectItem>
                <SelectItem value="deposito">Reingreso</SelectItem>
                <SelectItem value="utilidades">Utilidades de socio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Fecha</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="bulk-compensate-date" className="mt-1.5 rounded-none border-slate-300" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Nota (opcional)</Label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ej. Nómina 1a quincena oct" data-testid="bulk-compensate-note" className="mt-1.5 rounded-none border-slate-300" />
        </div>
        <p className="text-[11px] leading-relaxed text-slate-500">{HINTS[form.method]}</p>
        <DialogFooter>
          <button
            onClick={submit}
            disabled={saving || !form.date}
            data-testid="bulk-compensate-submit"
            className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Compensar {money(total)}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
